import React, { useEffect, useRef, useState } from "react";
import { X, Paperclip, Bug as BugIcon, FileText } from "lucide-react";
import { api, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES, PRIORITIES } from "@/components/FeedbackBadge";
import useFeedbackApps from "@/hooks/useFeedbackApps";
import { priorityLabel, categoryLabel } from "@/lib/labels";

// Reusable report form. channelId optional (set when launched from a channel).
export default function FeedbackModal({ workspaceId, channelId = null, initialTitle = "", onClose, onCreated }) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Bug");
  const [priority, setPriority] = useState("Medium");
  // Meniul e registrul de proiecte al workspace-ului plus valoarea transversală,
  // amândouă calculate pe server. Nu mai există intrare liberă: o aplicație
  // scrisă de mână ajungea într-un filtru, lângă numele de produse.
  const [projects, setProjects] = useState([]);
  const [appChoice, setAppChoice] = useState("");
  const [appError, setAppError] = useState("");
  const { allApps } = useFeedbackApps(workspaceId);
  const [shots, setShots] = useState([]);        // file records {id, name, content_type}
  const [visibleToGuests, setVisibleToGuests] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);
  const titleRef = useRef(null);

  // appChoice ține „project:<id>" pentru un proiect din registru, sau valoarea
  // transversală ca atare.
  const chosenProject = projects.find((p) => `project:${p.id}` === appChoice) || null;
  const effectiveApp = () => (chosenProject ? chosenProject.name : appChoice.trim());
  const validateApp = () => {
    const ok = !!effectiveApp();
    setAppError(ok ? "" : "Aplicația e obligatorie");
    return ok;
  };

  const isTitleValid = !!title.trim();
  const isAppValid = !!effectiveApp();
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/ticket-projects", { params: { workspace_id: workspaceId } })
      .then(({ data }) => {
        if (!active) return;
        setProjects(data || []);
        if ((data || []).length === 1) setAppChoice(`project:${data[0].id}`);
      })
      .catch(() => { /* meniul rămâne cu valoarea transversală */ });
    return () => { active = false; };
  }, [workspaceId]);

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose?.();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const rec = await uploadFile(workspaceId, f);
        setShots((prev) => [...prev, rec]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Încărcarea nu a reușit");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!isTitleValid) { toast.error("Titlul e obligatoriu"); titleRef.current?.focus(); return; }
    if (!validateApp()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/feedback", {
        workspace_id: workspaceId,
        channel_id: channelId,
        title: title.trim(),
        description,
        category,
        priority,
        app: effectiveApp(),
        project_id: chosenProject ? chosenProject.id : null,
        screenshots: shots.map((s) => s.id),
        visible_to_guests: visibleToGuests,
      });
      toast.success("Bug raportat ✅");
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Raportul nu a putut fi trimis");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onMouseDown={onClose}
      data-testid="feedback-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      aria-describedby="feedback-modal-description"
      ref={dialogRef}
      tabIndex={-1}
      onKeyDownCapture={handleDialogKeyDown}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-surface border border-line/10 shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="h-12 px-4 flex items-center justify-between border-b border-line/10 sticky top-0 bg-surface">
          <div className="flex items-center gap-2">
            <BugIcon className="w-4 h-4 text-brand" />
            <h3 id="feedback-modal-title" className="font-bold tracking-tight">Trimite feedback</h3>
          </div>
          <button type="button" onClick={onClose} className="text-fg/50 hover:text-fg" data-testid="feedback-modal-close" aria-label="Închide dialogul de feedback"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <p id="feedback-modal-description" className="sr-only">Raportează un defect sau o observație despre produs. Titlul și aplicația sunt obligatorii.</p>
          <div>
            <label htmlFor="feedback-title-input" className="label-tiny block mb-1.5">Titlu *</label>
            <input
              id="feedback-title-input"
              ref={titleRef}
              data-testid="feedback-title"
              value={title} maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Rezumat scurt"
              className="input-flat"
              required
              aria-required="true"
              aria-invalid={!isTitleValid}
            />
          </div>

          <div>
            <label htmlFor="feedback-description-input" className="label-tiny block mb-1.5">Descriere <span className="text-fg/30">(markdown)</span></label>
            <textarea
              id="feedback-description-input"
              data-testid="feedback-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={4} placeholder="Ce s-a întâmplat? Pași de reproducere, ce te așteptai și ce s-a întâmplat…"
              className="input-flat resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="feedback-category-select" className="label-tiny block mb-1.5">Categorie *</label>
              <select id="feedback-category-select" data-testid="feedback-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-flat" required>
                {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="feedback-priority-select" className="label-tiny block mb-1.5">Prioritate *</label>
              <select id="feedback-priority-select" data-testid="feedback-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="input-flat" required>
                {PRIORITIES.map((p) => <option key={p} value={p}>{priorityLabel(p)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="feedback-app-select" className="label-tiny block mb-1.5">Proiect *</label>
            <select
              id="feedback-app-select"
              data-testid="feedback-app"
              value={appChoice}
              onChange={(e) => { setAppChoice(e.target.value); setAppError(""); }}
              onBlur={validateApp}
              className="input-flat"
              required
              aria-required="true"
              aria-invalid={!!appError || !isAppValid}
              aria-describedby={appError ? "feedback-app-error" : undefined}
            >
              <option value="" disabled>Alege aplicația…</option>
              {projects.map((p) => (
                <option key={p.id} value={`project:${p.id}`}>{p.name}</option>
              ))}
              {allApps && <option value={allApps}>{allApps}</option>}
            </select>
            {appError && <p data-testid="feedback-app-error" className="text-[11px] text-destructive mt-1">{appError}</p>}
          </div>

          <div>
            <label id="feedback-attachments-label" className="label-tiny block mb-1.5">Atașamente</label>
            <label data-testid="feedback-attach" className="inline-flex items-center gap-1.5 text-xs text-fg/50 hover:text-fg cursor-pointer">
              <Paperclip className="w-3.5 h-3.5" /> {uploading ? "se încarcă…" : "adaugă imagini sau documente"}
              <input type="file" multiple onChange={onPick} className="hidden" aria-labelledby="feedback-attachments-label" />
            </label>
            {shots.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {shots.map((s) => (
                  <div key={s.id} className="bg-surface-2 border border-line/10 px-2 py-1 flex items-center gap-1.5 text-[11px]">
                    <FileText className="w-3 h-3 text-brand" />
                    <span className="mono truncate max-w-[140px]">{s.name}</span>
                    <button type="button" onClick={() => setShots((prev) => prev.filter((x) => x.id !== s.id))} className="text-fg/40 hover:text-red-400" aria-label={`Elimină atașamentul ${s.name}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label data-testid="feedback-guest-visibility" className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={visibleToGuests}
              onChange={(e) => setVisibleToGuests(e.target.checked)}
            />
            <span className="text-xs">
              Vizibil pentru Guests
              <span className="block text-fg-muted">Implicit ticketele nu sunt vizibile conturilor de guest.</span>
            </span>
          </label>
        </div>

        <div className="px-4 py-3 border-t border-line/10 flex items-center justify-end gap-2 sticky bottom-0 bg-surface">
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 text-fg/60 hover:text-fg">Anulează</button>
          <button
            data-testid="feedback-submit"
            type="submit"
            disabled={title.trim().length === 0 || effectiveApp().length === 0 || saving || uploading}
            className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-disabled={title.trim().length === 0 || effectiveApp().length === 0 || saving || uploading}
          >
            {saving ? "Submitting…" : "Submit report"}
          </button>
        </div>
      </form>
    </div>
  );
}
